<template>
	<div class="chart chart--heatmap" ref="chart">
		<svg id="heatmap" :viewBox="viewBox" preserveAspectRatio="xMinYMin">
			<defs v-if="isOverflowing">
				<linearGradient id="maskLeft" x1="0%" y1="0%" x2="100%" y2="0%">
					<stop offset="0%" style="stop-color:rgb(255,255,255); stop-opacity: 1" />
					<stop offset="100%" style="stop-color:rgb(255,255,255); stop-opacity: 0" />
				</linearGradient>
				<linearGradient id="maskRight" x1="0%" y1="0%" x2="100%" y2="0%">
					<stop offset="0%" style="stop-color:rgb(255,255,255); stop-opacity: 0" />
					<stop offset="100%" style="stop-color:rgb(255,255,255); stop-opacity: 1" />
				</linearGradient>
			</defs>
			<g id="slider" :transform="'translate(' + animatedSliderPos + ', 0)'">

				<!-- Labels for X axis -->
				<g 
				class="labels labels--x"
				:transform="'translate(' + margin.left + ',' + (rotateXLabels ? rotateXLabelsOffset : 0) + ')'"
				>
					<g 
					class="label label--x" 
					v-for="(label, l) in xLabels"
					:key="'labelX-' + l" 
					:class="rotateXLabels ? 'label--rotate' : ''" 
					:transform="'translate(' + ((l * squareWidth) + squareWidth / 2 - ((label.length - 1) * squareWidth *.15)) + ',0)'"
					:style="{ fontSize: 'clamp(' + (fontSize.min * .85) + 'px,' + fontSize.med *.7 + 'px,' + fontSize.max + 'px)' }" 
					>
						<text
						class="label__row"
						v-for="(row, r) in label"
						:key="'labelX-' + l + '-' + r"
						x="0"
						:y="r * squareWidth / 3.5" :transform="rotateXLabels ? 'rotate(-50)' : false"
						v-html="row">
						</text> 
					</g>
				</g>

				<!-- Squares -->
				<g
				class="squares"
				ref="grid"
				:transform="'translate(' + margin.left + ',' + margin.top + ')'"
				>
					<g 
					class="squares__line" 
					v-for="(line, l) in lines"
					:key="'line-' + l"
					:transform="'translate(0,' + (l * squareWidth) + ')'"
					>
						<rect 
						v-for="(value, v) in line"
						:key="'square' + '-' + l + '-' + v" 
						class="square"
						ref="squares"
						:x="v * squareWidth" 
						y=0
						:fill="value > 0 ? colorScale(value) : 'white'"
						:width="squareWidth"
						:height="squareWidth"
						/>
					</g>
				</g>

				<!-- Values in squares -->
				<g 
				class="labels labels--squares"
				:transform="'translate(' + margin.left + ',' + margin.top + ')'">
					<g 
					class="labels__line"
					v-for="(line, l) in lines"
					:key="'label-line-' + l"
					:transform="'translate(0,' + (l * squareWidth) + ')'"
					>
						<text
						class="label label--square"
						v-for="(value, v) in line"
						:key="'value' + '-' + l + '-' + v"
						:x="(v * squareWidth) + squareWidth / 2"
						:y="squareWidth / 2"
						:fill="value > (maxValue - minValue) * colorThreshold ? colorHighValues : colorLowValues"
						:style="{ fontSize: 'clamp(' + fontSize.min + 'px,' + fontSize.med + 'px,' + fontSize.max + 'px)' } "
						>
							{{!!value && showValues ? toLocale(value, 1) : ''}}
						</text>
					</g>
				</g>
			</g>

			<!-- Previous and next buttons -->
			<g
			id="previous"
			v-if="showPrev"
			@click="!isInTransition ? moveSlider('left') : ''"
			:transform="'translate(' + margin.left  + ',0)'"
			>
				<rect
				x="-1"
				y="0"
				:width="size.width / 10"
				fill="url(#maskLeft)"
				:height="height - margin.bottom" 
				/>
				<path
				:fill="chevronColor" 
				:transform="'translate(0,' + ((height - margin.bottom) / 2) + ') scale(' + chevronConfig.scale + ')'"
				d="M7.37546959,182.012507 L19.5526695,168.668992 C115.094221,64.0199375 165.610605,9.74834552 171.101824,5.85421628 C179.681853,-0.230360662 194.959023,-3.50278372 204.316023,5.85421628 C213.673023,15.2112163 204.593533,32.5297014 195.258533,41.9087014 L55.2161242,198.747066 L200.880721,356.392182 C210.215721,365.771182 210.253721,381.091382 200.880721,390.464382 C191.507721,399.837382 176.311721,399.837382 166.939721,390.464382 L7.37646959,215.953507 C-1.99653041,206.581507 -1.99653041,191.385507 7.37546959,182.012507 Z"
				/>	
			</g>
			<g
			id="next"
			v-if="showNext"
			@click="!isInTransition ? moveSlider('right') : ''"
			:transform="'translate(' + (size.width - (size.width / 10)) + ',0)'"
			>
				<rect
				x="0"
				y="0"
				:width="size.width / 10"
				fill="url(#maskRight)"
				:height="height - margin.bottom"
				/>
				<path
				:fill="chevronColor"
				:transform="'translate(' + ((size.width / 10) - chevronConfig.offset) +  ',' + ((height - margin.bottom) /2) + ') scale(' + chevronConfig.scale + ')'"
				d="M201.221754,215.481626 C97.7908705,328.864101 43.2154191,387.583531 37.4953999,391.639916 C28.915371,397.724493 13.6382002,400.996916 4.2812002,391.639916 C-5.0757998,382.282916 4.00369082,364.964431 13.3386908,355.585431 L153.381099,198.747066 L7.71650243,41.1019507 C-1.61849757,31.7229507 -1.65649757,16.40275 7.71650243,7.02975 C17.0895024,-2.34325 32.2855024,-2.34325 41.6575024,7.02975 L201.220754,181.540626 C210.593754,190.912626 210.593754,206.108626 201.221754,215.481626 Z" 
				/>
			</g>

			<!-- Line labels -->
			<rect
			id="mask"
			x="0"
			y="0"
			:height="height"
			:width="margin.left" 
			fill="white"
			/>
			<g
			class="labels labels--line"
			:transform="'translate(' + (!!flags.length ? (flagSize.width + squareWidth / 5) : 0) + ',' + margin.top + ')'" >
				<text
				class="label label--line"
				v-for="(label, l) in labels"
				:key="'label-' + l"
				x="0"
				:y="l * squareWidth + squareWidth / 2"
				:style="{ fontSize: 'clamp(' + fontSize.min + 'px,' + (fontSize.med) + 'px,' + fontSize.max + 'px)' }"
				>
					{{label}}
				</text>
			</g>

			<!-- Line flags -->
			<g
			class="flags"
			:transform="'translate(0,' + margin.top + ')'"
			v-if="!!flags.length"
			>
				<g
				class="flag"
				v-for="(flag, f) in flags"
				:key="'flag-' + f"
				:transform="'translate(0,' + (f * squareWidth) + ')'"
				>
					<rect
					class="flag__box"
					x="0"
					:y="(squareWidth / 2) - (flagSize.height / 2)"
					:width="flagSize.width"
					:height="flagSize.height"
					:fill="flag.color"
					/>
					<text
					class="flag__text"
					:x="flagSize.width / 2"
					:y="(squareWidth / 2) + 1"
					:style="{ fontSize: 'clamp(' + fontSize.min * .9 + 'px,' + (fontSize.med * .7) + 'px,' + fontSize.max + 'px)' }"
					>
						{{flag.text}}
					</text>
				</g>
			</g>
		</svg>
	</div>
</template>
<script>
	import gsap from 'gsap'
	import { scaleLinear, max, min } from 'd3'
	import { toLocale } from 'global/js/apps.js'
	export default {
		name: 'Heatmap',
		props: {
			size: {
				type: Object,
				default: () => {
					return { width: 800 }
				}
			},
			margin: {
				type: Object,
				default: () => {
					return { top: 30, right: 0, bottom: 40, left: 100 }
				},
			},
			lines: {
				type: Array,
				default: () => [
					[5, 7, 9, 6, 7, 8, 10, 12, 6],
					[12, 11, 10, 10, 10, 11, 12, 13, 11],
					[20, 21, 19, 22, 21, 19, 17, 16, 19],
				]
			},
			labels: {
				type: Array,
				default: () => ['Line 1', 'Line 2', 'Line 3']
			},
			xLabels: {
				type: Array,
				default: () => [
					['Element', 'Number 1'],
					['Element', 'Number 2'],
					['Element', 'Number 3'],
					['Element', 'Number 4'],
					['Element', 'Number 5'],
					['Element', 'Number 6'],
					['Element', 'Number 7'],
					['Element', 'Number 8'],
					['Element', 'Number 9'],
				]
			},
			flags: {
				type: Array,
				default: () => []
			},
			colorRange: {
				type: Array,
				default: () => ['#ecf2f9', '#b3cce6', '#33669a']
			},
			colorLowValues: {
				type: String,
				default: 'black',
			},
			colorHighValues: {
				type: String,
				default: 'white',
			},
			colorThreshold: {
				type: Number,
				default: .7
			},
			chevronColor: {
				type: String,
				default: '#888'
			}, 
			rotateXLabels: {
				type: Boolean,
				default: false,
			},
			rotateXLabelsOffset: {
				type: Number,
				default: 45
			},
			showValues: {
				type: Boolean,
				default: true,
			},
			parentWidth: {
				type: Number,
				default: null,
			},
			mobile: {
				type: Boolean,
				default: false,
			},
			smallScreen: {
				type: Boolean,
				default: false,
			}
		},
		data: function() {
			return {
				maxWidth: 0,
				gridWidth: 0,
				showNext: false,
				showPrev: false,
				sliderPos: 0,
				tweenedSliderPos: 0, 
				isInTransition: false,
				isOverflowing: false,
				squareWidthSize: {
					min: 30,
					max: 40
				},
				chevronConfig: {},
				rwdChevronConfig: {
					desktop: { scale: .05, offset: 21},
					mobile: { scale: .2, offset: 40},
					smallScreen: { scale: .15, offset: 30}, 
				}
			}
		},
		computed: {
			squareWidth: function() {
				let min = this.squareWidthSize.min
				if (this.parentWidth) {
					min = parseInt(40000 / this.parentWidth)
					if (!this.showValues) min = parseInt(this.parentWidth / 100)
				} else {
					if (!this.showValues) min = 10
				}
				let width = parseInt((this.size.width - this.margin.right - this.margin.left) / this.nbSquares)
				if (width < min) return min
				if (width > this.squareWidthSize.max) return this.squareWidthSize.max

				return width
			},
			flagSize: function() {

				return {					
					width: this.squareWidth * .8,
					height: this.squareWidth * .4
				}
			},
			fontSize: function() {
				if (this.mobile) return { min: 10, med: (this.squareWidth / 3.5),	max: 40 }
				if (this.smallScreen) return { min: 9, med: (this.squareWidth / 2.5),	max: 20 }

				return { min: 9, med: (this.squareWidth / 3),	max: 20 }		
			},
			viewBox: function() {

				return '0, 0, ' + this.size.width + ', ' + this.height
			},
			nbSquares: function() {
		
				return this.lines.reduce((agg, line) => {
					if (line.length > agg) return line.length

					return agg
				}, 1)
			},
			maxValue: function() {

				return max(this.lines.reduce((agg, line) => {
					return agg.concat(line)
				}, []))				
			},
			minValue: function() {

				return min(this.lines.reduce((agg, line) => {
					return agg.concat(line.filter(v => v > 0))
				}, []))				
			},
			colorScale: function() {

				return scaleLinear()
					.domain([this.minValue, this.maxValue / 3, this.maxValue ])
					.range(this.colorRange)
			},
			height: function() {

				return this.margin.top + this.margin.bottom + (this.squareWidth * this.lines.length)
			},
			animatedSliderPos: function() {

				return this.tweenedSliderPos.toFixed(0);
			}
		},
		methods: {
			toLocale(value) {
				return toLocale(value, 3)
			},
			moveSlider(direction) {
				if (direction == 'left') this.sliderPos += this.squareWidth * 10
				if (direction == 'right') this.sliderPos -= this.squareWidth * 10	
			},
			checkOverflow: function() {
				this.gridWidth = this.$refs.grid.getBBox().width
				this.maxWidth = this.size.width - this.margin.right - this.margin.left
				if (this.gridWidth > this.maxWidth) {
					this.isOverflowing = true
					this.sliderPos = this.maxWidth - this.gridWidth
				}

			}
		},
		created() {
			this.chevronConfig = this.rwdChevronConfig.desktop
			if (this.smallScreen) this.chevronConfig = this.rwdChevronConfig.smallScreen
			if (this.mobile) this.chevronConfig =this.rwdChevronConfig.mobile
		},
		mounted() {
			this.checkOverflow()
		},
		watch: {
			lines: function() {
				this.sliderPos = 0
				this.$nextTick(() => {
					this.checkOverflow()
				})
			},
			sliderPos: function(pos) {
				let animDuration = .5
				gsap.to(this.$data, { duration: animDuration, tweenedSliderPos: pos, ease: 'power2' })
				this.isInTransition = true
				setTimeout(() => {
					this.isInTransition = false				
					let $lastSquare = this.$refs.squares[this.nbSquares - 1]
					let lastSquareRight = $lastSquare.getBBox().x + this.squareWidth + pos
					this.showNext = lastSquareRight > this.maxWidth
					let firstSquareLeft = $lastSquare.getBBox().x - ((this.nbSquares - 1) * this.squareWidth) + pos
					this.showPrev = firstSquareLeft < -1 
				}, 1000 * animDuration)
			} 
		}
	}
</script>
<style lang="scss">
	@import 'global/css/contexte.apps.colors';
	.square {
		stroke: white;
		stroke-width: .5;
	}
	.label {
		a {
			cursor: pointer;
			&:hover {
				text-decoration: underline;
			}
		}
		&--line {
			dominant-baseline: middle;
		}
		&--square {
			dominant-baseline: middle;
			text-anchor: middle;
		}
		&--x {
			text-transform: uppercase;
			font-size: .55em;
			text-anchor: middle;
		}
		&--rotate {
			text-anchor: start;	
		}
	}
	.flag {
		&__text {
			fill: white;
			text-transform: uppercase;
			text-anchor: middle;
			dominant-baseline: middle;
		}
	}
	#previous, #next {
		cursor: pointer;
	}
</style>